import useDocumentTitle from 'hooks/useDocumentTitle';
import { useEffect, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom';
import { updateCampaignsActivityFilters } from 'slices/campaigns/activity/reducer';
import { fetchCampaignsActivityThunk } from 'slices/campaigns/activity/thunk';
import { AppDispatch, RootState } from 'store'
import { CampaignActivity, CampaignsActivityFilters } from 'types/campaign/activity';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';
import "./campaign.scss";
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Badge, Col, Row } from 'reactstrap';
import { defaultFiltersValue } from 'Components/collaboration/milestone/Filters';
import FilterIcon from 'Components/icons/FilterIcon';
import CampaignFilters from 'Components/campaign/CampaignFilters';
import { useMediaQuery } from 'react-responsive';
import { isDesktopMediaQuery } from 'Components/constants/layout';
import CampaignCard from 'Components/campaign/CampaignCard';

const CampaignsActivities = () => {
    useDocumentTitle("Campaigns");
    const isDesktop = useMediaQuery(isDesktopMediaQuery);
    const dispatch: AppDispatch = useDispatch();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filtersModalVisibility, showFiltersModal] = useState(false);

    const queryFilters = searchParams.get('filters');

    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters) : {};
        dispatch(updateCampaignsActivityFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])

    const [searchQuery, setSearchQuery] = useState('');

    const filters = useSelector((state: RootState) => state.campaignsActivity.filters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']))


    const campaignsActivityData = useSelector((state: RootState) => state.campaignsActivity.filtered_lists[filterId!]);
    const campaigns = campaignsActivityData?.list ?? [];
    const page = campaignsActivityData?.page ?? 1;
    const has_more = campaignsActivityData?.has_more ?? true;
    const totalCount = campaignsActivityData?.count ?? 0;

    const loading = useSelector((state: RootState) => state.campaignsActivity.loading);
    const error = useSelector((state: RootState) => state.campaignsActivity.error);
    const [autoFetchReq, setAutoFetchRequest] = useState(false);

    const setFilters = (updatedFilters: CampaignsActivityFilters) => {
        const encodedFilters = encodeBase64(updatedFilters);
        setSearchParams({ filters: encodedFilters });
    }

    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        if (filters.page && filters.page === 1) {
            setInitialLoading(true);
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchCampaignsActivityThunk({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']));
                setInitialLoading(false);
            })
        }, 500)

    }, [filters])

    useEffect(() => {
        setSearchQuery(filters.q ?? '');
    }, [filters.q])

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchCampaignsActivityThunk({ filters: { ...filters, page: nextPageNumber } })).finally(() => setInitialLoading(false))
        }
    }

    useIdleTimer({
        timeout: 1000 * 60 * 5, // 5 minutes
        onActive: () => {
            setAutoFetchRequest(true);
            setTimeout(() => {
                fetchCampaignsActivity(1, true);
            }, 100)
        },
        throttle: 1000,
    });

    const fetchCampaignsActivity = async (page: number, force: boolean = false) => {
        dispatch(fetchCampaignsActivityThunk({ filters: { ...filters, page }, force })).finally(() => {
            setInitialLoading(false);
            setAutoFetchRequest(false);
        });
    }


    const forceRefetch = () => {
        setInitialLoading(true)
        fetchCampaignsActivity(1, true)
    }

    const searchCampaign = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    }

    const getFiltersQuery = (data: any) => {
        const encodedData = encodeBase64({ ...defaultFiltersValue, ...data, ...(filters.hub_id ? { hub_id: filters.hub_id, hub: filters.hub } : {}) });
        return `?filters=${encodedData}`;
    }


    const getAssigneeSteps = (campaign: CampaignActivity, assignee: string) => {
        const steps = campaign.steps ?? {};

        let assigneeStep: Record<string, { name: string, count: number, assignee: string }> = {};

        for (let [slug, step] of Object.entries(steps)) {
            if (step.assignee === assignee.toLowerCase()) {
                assigneeStep[slug] = step;
            }
        }

        return assigneeStep;

    }

    return (
        <div className="campaign-page">
            <div className="d-flex justify-content-between gap-2 align-items-md-center flex-column flex-md-row">
                <div className='d-flex gap-2 align-items-center'>
                    <h3 className="m-0">Campaigns Activities </h3> <Badge color='success' >{totalCount}</Badge>
                </div>
                <div className="d-flex gap-2">
                    <div className="campaign-layout-search-container">
                        <span>
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => searchCampaign(e.target.value)} className="campaign-layout-search-input" />
                    </div>
                    <button className='btn btn-white' onClick={() => showFiltersModal(true)}><FilterIcon className="me-1" /> Filters</button>
                </div>

            </div>
            <div className="campaign-page-content my-3">
                <InfiniteScroll
                    dataLength={campaigns.length}
                    next={nextPage}
                    className='row g-3 overflow-x-hidden'
                    hasMore={has_more}
                    loader={<></>}
                >
                    {
                        !initialLoading && campaigns.map((campaign, i) => {
                            return(
                                <CampaignCard campaign={campaign} filters={filters} key={i} />
                            )
                            // return (
                            //     <div key={campaign.uuid} className="campaign-card p-0">
                            //         <div className="d-flex justify-content-between align-items-stretch align-items-center flex-column flex-md-row">
                            //             <div className="p-3 d-flex align-items-center">
                            //                 <div className="d-flex gap-3 flex-wrap">
                            //                     <div className="d-flex flex-column justify-content-between flex-wrap">
                            //                         <div className="d-flex flex-column gap-2">
                            //                             <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id }) }} className="campaign-name text-primary m-0">{campaign.name}</Link>
                            //                             <span className="published-at">{campaign.agency?.name} {campaign.brand?.name ? `- ${campaign.brand.name}` : ''}</span>
                            //                             <div className="d-flex gap-2 flex-wrap">
                            //                                 {
                            //                                     (campaign.milestones ? [...campaign.milestones] : []).sort((a, b) => a.sort_order - b.sort_order).map((milestone, milestoneIndex) => <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, gig_type: milestone.gig_type.slug }) }} className="badge bg-primary-subtle text-primary" style={{ width: 'fit-content' }} key={`${campaign.uuid}-${milestoneIndex}`}>{milestone?.gig_type?.name ?? '-'}</Link>)
                            //                                 }
                            //                             </div>
                            //                         </div>
                            //                     </div>
                            //                 </div>
                            //             </div>
                            //             <div className="p-3 m-3 rounded-4 border bg-light d-flex justify-content-between align-items-center gap-3">
                            //                 <h5 className="text-muted text-uppercase fs-13 m-0">Ongoing Collabs</h5>
                            //                 <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id }) }}>
                            //                     <h4 className="mb-0 text-primary d-flex justify-content-between align-items-center w-100"><span className="counter-value">
                            //                         {campaign.ongoing_collaborations_count ?? 0}
                            //                     </span>
                            //                     </h4>
                            //                 </Link>
                            //             </div>
                            //             {/* <div className="border-0 card-header" style={{ width: '400px', maxWidth: '100%' }}>
                            //                 <div className="g-0 h-100 text-center row">
                            //                     <div className="col-4">
                            //                         <div className="d-flex flex-column align-items-center justify-content-center h-100 p-3 border border-dashed">
                            //                             <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, assignee: 'influencer' }) }}>
                            //                                 <h5 className="mb-1 text-primary"><span className="counter-value" data-target={campaign.assignee_steps?.influencer ?? 0}><span>{campaign.assignee_steps?.influencer ?? 0}</span></span></h5>
                            //                                 <p className="mb-0">Influencer</p>
                            //                             </Link>
                            //                         </div>
                            //                     </div>
                            //                     <div className="col-4">
                            //                         <div className="d-flex flex-column align-items-center justify-content-center h-100 p-3 border border-dashed border-start-0">
                            //                             <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, assignee: 'agency' }) }}>
                            //                                 <h5 className="mb-1 text-primary"><span className="counter-value" data-target={campaign.assignee_steps?.agency ?? 0}><span>{campaign.assignee_steps?.agency ?? 0}</span></span></h5>
                            //                                 <p className="mb-0">Agency</p>
                            //                             </Link>

                            //                         </div>
                            //                     </div>
                            //                     <div className="col-4">
                            //                         <div className="d-flex flex-column align-items-center justify-content-center h-100 p-3 border border-dashed border-start-0 border-end-0">
                            //                             <Link target='_blank' className='text-primary' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, assignee: 'hub' }) }}>
                            //                                 <h5 className="mb-1 text-primary"><span className="counter-value" data-target={campaign.assignee_steps?.hub ?? 0}><span>{campaign.assignee_steps?.hub ?? 0}</span></span></h5>
                            //                                 <p className="mb-0">Hub</p>
                            //                             </Link>

                            //                         </div>
                            //                     </div>
                            //                 </div>
                            //             </div> */}
                            //         </div>
                            //         <div className="p-3">
                            //             <div className="row g-2">
                            //                 {
                            //                     ['Influencer', 'Agency', 'Hub'].map((assignee, assigneeIndex) => (
                            //                         <div className="col-12 col-md-6 col-lg-4 d-flex flex-column" key={assigneeIndex}>
                            //                             <div className="p-3 border rounded-4 bg-light flex-fill">
                            //                                 <div className="d-flex justify-content-between align-items-center pb-2">
                            //                                     <h6>{assignee}</h6>
                            //                                     <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, assignee: assignee.toLowerCase() }) }}>
                            //                                         <h5 className="mb-1 text-primary"><span className="counter-value"><span>{campaign.assignee_steps?.[assignee.toLowerCase() as 'influencer' | 'agency' | 'hub'] ?? 0}</span></span></h5>
                            //                                     </Link>
                            //                                 </div>
                            //                                 {
                            //                                     Object.values(getAssigneeSteps(campaign, assignee)).length > 0 ? (
                            //                                         <Row>
                            //                                             <Col xxl={12}>
                            //                                                 {
                            //                                                     <div className="row g-2">
                            //                                                         {(Object.entries(getAssigneeSteps(campaign, assignee))).map(([slug, step], index: number) => (
                            //                                                             <div className='col-12 col-md-6' key={index}>
                            //                                                                 <div className="bg-white border" key={index}>
                            //                                                                     <div className="p-3 d-flex justify-content-between align-items-center">
                            //                                                                         <h5 className="text-muted text-uppercase fs-13 m-0">{step.name}</h5>
                            //                                                                         <div className="d-flex align-items-center">
                            //                                                                             <div className="flex-grow-1">
                            //                                                                                 <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, gig_step: slug }) }}>
                            //                                                                                     <h4 className="mb-0 text-primary d-flex justify-content-between align-items-center w-100"><span className="counter-value" data-target={step.count}>
                            //                                                                                         {step.count}
                            //                                                                                     </span>
                            //                                                                                     </h4>
                            //                                                                                 </Link>
                            //                                                                             </div>
                            //                                                                         </div>
                            //                                                                     </div>
                            //                                                                 </div>
                            //                                                             </div>
                            //                                                         ))}
                            //                                                     </div>
                            //                                                 }

                            //                                             </Col>
                            //                                         </Row>
                            //                                     )
                            //                                         : (
                            //                                             <div className="d-flex align-items-center justify-content-center w-100">
                            //                                                 <span className='text-muted'>No Pending Steps</span>
                            //                                             </div>
                            //                                         )
                            //                                 }
                            //                             </div>
                            //                         </div>
                            //                     ))
                            //                 }
                            //             </div>
                            //         </div>
                            //         {/* {
                            //             Object.values(campaign?.steps ?? {}).length > 0 &&
                            //             <Row className='p-3 bg-light-subtle'>
                            //                 <Col xxl={12}>
                            //                     <div className={`d-flex ${isDesktop ? 'mb-n4 pt-2' : 'py-2'}`} style={{ marginBottom: '2.5rem !important' }}>
                            //                         <h5 className="card-title fs-18 mb-0">Pending Steps</h5>
                            //                     </div>
                            //                     {
                            //                         isDesktop ? (
                            //                             <Swiper modules={[Navigation]}
                            //                                 slidesPerView={1}
                            //                                 spaceBetween={10}
                            //                                 navigation={{
                            //                                     nextEl: ".swiper-button-next",
                            //                                     prevEl: ".swiper-button-prev",
                            //                                 }}
                            //                                 breakpoints={{
                            //                                     640: {
                            //                                         slidesPerView: 2,
                            //                                         spaceBetween: 20,
                            //                                     },
                            //                                     768: {
                            //                                         slidesPerView: 3,
                            //                                         spaceBetween: 24,
                            //                                     },
                            //                                     1024: {
                            //                                         slidesPerView: 5,
                            //                                         spaceBetween: 24,
                            //                                     },
                            //                                 }}
                            //                                 loop={true}
                            //                                 className="mySwiper marketplace-swiper rounded gallery-light pt-5">
                            //                                 <div className="swiper-wrapper row row-cols-xxl-7 row-cols-md-5 row-cols-2 g-0">
                            //                                     {(Object.entries(campaign?.steps ?? {})).map(([slug, step], index: number) => (
                            //                                         <SwiperSlide key={index}>
                            //                                             <div className="col bg-white border" key={index}>
                            //                                                 <div className="p-3">
                            //                                                     <h5 className="text-muted text-uppercase fs-13">{step.name}</h5>
                            //                                                     <div className="d-flex align-items-center">
                            //                                                         <div className="flex-grow-1">
                            //                                                             <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, gig_step: slug }) }}>
                            //                                                                 <h4 className="mb-0 text-primary d-flex justify-content-between align-items-center w-100"><span className="counter-value" data-target={step.count}>
                            //                                                                     {step.count}
                            //                                                                 </span>
                            //                                                                 </h4>
                            //                                                             </Link>
                            //                                                         </div>
                            //                                                     </div>
                            //                                                 </div>
                            //                                             </div>
                            //                                         </SwiperSlide>
                            //                                     ))}
                            //                                 </div>
                            //                                 <div className="swiper-button-next mb-2"></div>
                            //                                 <div className="swiper-button-prev"></div>
                            //                             </Swiper>
                            //                         ) : (
                            //                             <div className="d-flex flex-column gap-2">
                            //                                 {(Object.entries(campaign?.steps ?? {})).map(([slug, step], index: number) => (
                            //                                     <div key={index}>
                            //                                         <div className="col bg-white border" key={index}>
                            //                                             <div className="p-3 d-flex justify-content-between align-items-center">
                            //                                                 <h5 className="text-muted text-uppercase fs-13 m-0">{step.name}</h5>
                            //                                                 <div className="d-flex align-items-center">
                            //                                                     <div className="flex-grow-1">
                            //                                                         <Link target='_blank' to={{ pathname: '/tasks', search: getFiltersQuery({ campaign_id: campaign.id, gig_step: slug }) }}>
                            //                                                             <h4 className="mb-0 text-primary d-flex justify-content-between align-items-center w-100"><span className="counter-value" data-target={step.count}>
                            //                                                                 {step.count}
                            //                                                             </span>
                            //                                                             </h4>
                            //                                                         </Link>
                            //                                                     </div>
                            //                                                 </div>
                            //                                             </div>
                            //                                         </div>
                            //                                     </div>
                            //                                 ))}
                            //                             </div>
                            //                         )
                            //                     }

                            //                 </Col>
                            //             </Row>
                            //         } */}
                            //     </div>
                            // )
                        })
                    }
                    {
                        (loading || initialLoading) && Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} height={150} className='w-100' />
                        ))
                    }
                    {
                        !loading && !initialLoading && error &&
                        <div className="d-flex justify-content-center align-items-center">
                            <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                        </div>
                    }
                </InfiniteScroll>
                {
                    !loading && !initialLoading && campaigns.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center">
                        <div className="length-zero">
                            <h3>Currently there is no campaign in your list</h3>
                        </div>
                    </div>
                }
            </div>
            <CampaignFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(updatedFilters) => setFilters(updatedFilters)}
            />
        </div>
    )
}

export default CampaignsActivities;